/**
 * Feedback surfaces: Modal, Toast, EmptyState, ErrorState, LoadingState.
 *
 * The console's register is calm and procedural. No celebration, no illustrated hero moments —
 * that is the app's register, not an operator's. An empty state is one quiet line and exactly one
 * constructive action, and it never invents an action where none exists.
 */
import { createContext, useContext, useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { Icon, Button, Skeleton } from './primitives';
import type { IconName } from './icons';

/* ────────────────────────────  Modal  ──────────────────────────── */

export interface ModalProps {
  open?: boolean;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
  width?: number;
  onClose?: () => void;
  style?: CSSProperties;
}

/**
 * Escape closes, focus moves in on open and returns on close. The scrim is the ink-tinted overlay
 * token, never grey and never frosted glass.
 */
export function Modal({ open = true, title, children, actions, width = 560, onClose, style }: ModalProps) {
  const panel = useRef<HTMLDivElement>(null);
  const returnTo = useRef<Element | null>(null);

  /* The handler is read through a ref so this effect depends on `open` alone. Keying it on
     `onClose` too would re-run it whenever the parent re-rendered with a fresh inline callback —
     and the cleanup restores focus, so a dialog containing a text field would lose focus after
     every keystroke that re-rendered the screen behind it. */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement;
    panel.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      (returnTo.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--c-overlay)',
        padding: 'var(--sp-lg)',
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          width: '100%',
          maxWidth: width,
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--c-surface)',
          border: '1px solid var(--c-border)',
          borderRadius: 'var(--r-card)',
          boxShadow: 'var(--sh-lg)',
          display: 'grid',
          gap: 'var(--sp-lg)',
          padding: 'var(--sp-xxl)',
          ...style,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-md)' }}>
          <h2
            style={{
              margin: 0,
              flex: 1,
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--fs-admin-section)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--c-ink)',
            }}
          >
            {title}
          </h2>
          {onClose && (
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              style={{
                display: 'grid',
                placeItems: 'center',
                width: 32,
                height: 32,
                border: 'none',
                background: 'none',
                color: 'var(--c-ink3)',
                cursor: 'pointer',
              }}
            >
              <Icon name="x" size={16} />
            </button>
          )}
        </div>
        {children}
        {actions && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-sm)' }}>{actions}</div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────  Toast  ──────────────────────────── */

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastProps {
  tone?: ToastTone;
  message: string;
  /** Label for the one action a toast may carry — usually Undo. */
  action?: string;
  onAction?: () => void;
  style?: CSSProperties;
}

const TOAST_TONE: Record<ToastTone, { bg: string; fg: string; border: string; icon: IconName }> = {
  success: { bg: 'var(--c-success-bg)', fg: 'var(--c-success-text)', border: 'var(--c-success)', icon: 'circle-check' },
  error: { bg: 'var(--c-error-bg)', fg: 'var(--c-error-text)', border: 'var(--c-error)', icon: 'circle-alert' },
  info: { bg: 'var(--c-info-bg)', fg: 'var(--c-info-text)', border: 'var(--c-info)', icon: 'info' },
};

export function Toast({ tone = 'info', message, action, onAction, style }: ToastProps) {
  const t = TOAST_TONE[tone];
  return (
    <div
      role="status"
      // Errors interrupt; everything else waits its turn.
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-sm)',
        padding: 'var(--sp-md) var(--sp-lg)',
        borderRadius: 'var(--r-tile)',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.border}`,
        boxShadow: 'var(--sh-md)',
        fontSize: 'var(--fs-admin-ui)',
        ...style,
      }}
    >
      <Icon name={t.icon} size={16} />
      <span style={{ flex: 1 }}>{message}</span>
      {action && (
        <button
          type="button"
          onClick={onAction}
          style={{
            border: 'none',
            background: 'none',
            color: 'inherit',
            font: 'inherit',
            fontWeight: 'var(--fw-semibold)',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {action}
        </button>
      )}
    </div>
  );
}

/* ────────────────────────────  EmptyState  ──────────────────────────── */

export interface EmptyStateProps {
  icon?: IconName;
  title: string;
  body?: string;
  /** Exactly one constructive action, or none. Never invent one where none exists. */
  action?: ReactNode;
  inline?: boolean;
  style?: CSSProperties;
}

/* True inside a Section body with `pad={false}` — that body is sized for a table, so an inline
   empty state there has to carry the section's inset itself or it sits against the card edge. */
export const UnpaddedBodyContext = createContext(false);

export function EmptyState({ icon, title, body, action, inline = false, style }: EmptyStateProps) {
  const unpadded = useContext(UnpaddedBodyContext);
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--sp-sm)',
        justifyItems: inline ? 'start' : 'center',
        textAlign: inline ? 'left' : 'center',
        padding: inline ? (unpadded ? 'var(--sp-lg)' : 0) : 'var(--sp-xl) 0',
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={24} style={{ color: 'var(--c-ink3)' }} />}
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--fs-admin-card-heading)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--c-ink)',
        }}
      >
        {title}
      </span>
      {body && (
        <p style={{ margin: 0, maxWidth: 460, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>{body}</p>
      )}
      {action}
    </div>
  );
}

/* ────────────────────────────  ErrorState  ──────────────────────────── */

export interface ErrorStateProps {
  title: string;
  /** The API's `detail` prose, shown verbatim — never a rewritten guess at what went wrong. */
  cause?: string;
  /** The echoed X-Request-Id, so support has a handle into the logs. */
  requestId?: string;
  action?: ReactNode;
  secondary?: ReactNode;
  inline?: boolean;
  style?: CSSProperties;
}

export function ErrorState({ title, cause, requestId, action, secondary, inline = false, style }: ErrorStateProps) {
  return (
    <div
      role="alert"
      style={{
        display: 'grid',
        gap: 'var(--sp-sm)',
        justifyItems: inline ? 'start' : 'center',
        textAlign: inline ? 'left' : 'center',
        padding: inline ? 'var(--sp-md)' : 'var(--sp-xl)',
        border: '1px solid var(--c-error)',
        background: 'var(--c-error-bg)',
        borderRadius: 'var(--r-tile)',
        ...style,
      }}
    >
      <Icon name="circle-alert" size={20} style={{ color: 'var(--c-error)' }} />
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--fs-admin-card-heading)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--c-error-text)',
        }}
      >
        {title}
      </span>
      {cause && (
        <p style={{ margin: 0, maxWidth: 520, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-error-text)' }}>
          {cause}
        </p>
      )}
      {requestId && (
        <span className="mono-num" style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-error-text)' }}>
          request {requestId}
        </span>
      )}
      {(action || secondary) && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', marginTop: 'var(--sp-xs)' }}>
          {action}
          {secondary}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────  LoadingState  ──────────────────────────── */

export interface LoadingStateProps {
  variant?: 'skeleton' | 'fullscreen';
  rows?: number;
  message?: string;
  /** Shown only on a long wait, to say the delay is expected rather than broken. */
  reassurance?: string;
  style?: CSSProperties;
}

/** Content-shaped skeletons, never a spinner over stale content. */
export function LoadingState({ variant = 'skeleton', rows = 3, message, reassurance, style }: LoadingStateProps) {
  if (variant === 'fullscreen') {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{ display: 'grid', gap: 'var(--sp-sm)', placeItems: 'center', padding: 'var(--sp-x4l)', ...style }}
      >
        <Icon name="refresh-cw" size={20} style={{ color: 'var(--c-saffron)' }} />
        {message && <span style={{ fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>{message}</span>}
        {reassurance && (
          <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>{reassurance}</span>
        )}
      </div>
    );
  }
  return (
    <div role="status" aria-live="polite" aria-label={message ?? 'Loading'} style={{ display: 'grid', gap: 'var(--sp-sm)', ...style }}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={i === 0 ? 20 : 14} width={i === 0 ? '40%' : '100%'} />
      ))}
    </div>
  );
}

/** Re-exported so screens import one feedback module rather than two. */
export { Button };
